/**
 * The Refused photos tab's pure model: types, labels and rules (spec 2026-10-13 §6).
 * No imports, so the render tests can bundle the card without the auth provider.
 * refused-photos-api.ts re-exports all of it.
 */
export type Section = 'public' | 'private';

export interface QuarantineCheck {
  check_type: string;
  result: string;
  details: unknown;
}

/** One refused photo as backend/src/routes/photo-quarantine-admin.ts lists it. */
export interface QuarantineItem {
  photo_id: string;
  user_id: string;
  display_name: string | null;
  email: string;
  reasons: string[];
  blurred: boolean;
  /** Unblurred rows only: a 300 s presigned URL. A blurred row NEVER gets one here. */
  preview_url: string | null;
  /** Blurred rows only: a server-rendered blur (data: URL). */
  blurred_preview: string | null;
  requested_visibility: Section | null;
  /** Set when the upload was meant to replace a photo: that photo stays, this one is added. */
  replacing_photo_id: string | null;
  target_section: Section;
  section_full: boolean;
  created_at: string;
  /** null: kept with no time limit (owner decision 2026-10-19). */
  expires_at: string | null;
  /** false once the file left quarantine (restored, or deleted under the former 15-day rule). */
  file_kept?: boolean;
  /** When a reviewer decided it; null while it waits. */
  decided_at?: string | null;
  decision: string;
  checks: QuarantineCheck[];
}

export interface QuarantineList {
  data: QuarantineItem[];
  total: number;
  page: number;
  limit: number;
}

export const MIN_REVEAL_REASON = 5;

/** What the operator reads for each code these routes send. */
export const REFUSED_ERROR_TEXT: Readonly<Record<string, string>> = {
  reveal_required: 'Reveal the photo before you restore it.',
  section_full: "The member's section is full. The photo stays in review until there is room.",
  already_decided: 'Another reviewer has already decided this photo. The list is refreshed.',
  quarantine_expired: 'This photo was deleted under the former 15-day rule.',
  quarantine_file_gone: 'The file of this photo was deleted under the former 15-day rule.',
  quarantine_not_found: 'This refused photo is no longer waiting for review.',
  photo_already_registered: "A photo with this id is already on the member's account.",
  reveal_reason_required: 'Say why you need to see this photo (at least 5 characters).',
  legal_hold_copy_failed: 'The photo could not be put on legal hold. Nothing changed. Try again.',
  restore_copy_failed: 'The photo could not be restored. Nothing changed. Try again.',
};
/** Why Restore is disabled, or null. The server enforces both; the tab says it before the click. */
export function restoreBlockReason(item: QuarantineItem, revealedHere: boolean): string | null {
  if (item.section_full) {
    return `The member's ${item.target_section} photos are full. The photo stays in review until there is room.`;
  }
  if (item.blurred && !revealedHere) return REFUSED_ERROR_TEXT.reveal_required;
  return null;
}

export function timeLeftLabel(expiresAt: string | null, now: Date = new Date()): string {
  // Refused photos are kept with no time limit since 2026-10-19 (the API answers null).
  if (expiresAt === null) return 'No time limit';
  const ms = new Date(expiresAt).getTime() - now.getTime();
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) return 'Expiring';
  if (hours < 24) return `${hours} h left`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} left`;
}

export const REASON_LABELS: Readonly<Record<string, string>> = {
  explicit_content: 'Explicit',
  contact_information: 'Contact info',
  photo_subject: 'Photo subject',
};
